import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as VlbcCloudIcon } from '../../assets/icons/lbc-cloud-black-small.svg';
import { ReactComponent as PlugBlackIcon } from '../../assets/icons/plug-black.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as RoundQuestionBlackIcon } from '../../assets/icons/round-question-black.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import { fetchVlbcs, updateVlbcStatusRedux, removeVlbc } from '../../redux/slices/VlbcSlice';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import PageTitle from '../../shared-components/page-title/PageTitle';
import vlbcApi from '../../utils/api/vlbc';

import AddVlbc from './components/AddVlbc';
import EditVlbc from './components/EditVlbc';
import ListVlbc from './components/ListVlbc';

interface ModalBoxDetail {
  title: string | null;
  header: boolean;
  content: JSX.Element | null;
  notificationModal: boolean;
}

interface AssignChargersCount {
  matchedChargingPoints: number;
  unMatchedChargingPoints: number;
  vlbcTotalCount: number;
}
interface HeaderSupportProperties {
  chargerAssignCounts: AssignChargersCount;
}
/**
 * Header support content
 * @param {any} properties - The properties
 */
function HeaderSupportContent(properties: HeaderSupportProperties) {
  const { chargerAssignCounts } = properties;
  const [isHovered, setIsHovered] = useState(false);
  const { t } = useTranslation('vlbc');

  return (
    <div className="header-support-content">
      <div className="header-group">
        <span
          className="load-balancer-container-hover"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}>
          <RoundQuestionBlackIcon />
          {isHovered && (
            <div className="load-balancer-container">
              <div className="section">
                <h3 className="title">{t('virtualLoadBalancer')}</h3>
                <p className="description">{t('loadBalancerDescription')}</p>
                <div className="section-border" />
              </div>
              <div className="section">
                <h4 className="subtitle">{t('algorithm')}</h4>
                <div className="algo-block">
                  <h4>{t('equalShare')}</h4>
                  <p>{t('algorithmDescription')}</p>
                </div>
              </div>
            </div>
          )}
        </span>
        <div className="content-wrap">
          <div className="content">
            <span>
              <VlbcCloudIcon />
            </span>
            <div>{t('noOfVLB')}</div>
            <div className="count">
              <span>{chargerAssignCounts.vlbcTotalCount}</span>
            </div>
          </div>
          <div className="content">
            <span>
              <PlugBlackIcon />
            </span>
            <div>{t('noOfChargersAssigned')}</div>
            <div className="count">
              <span>{chargerAssignCounts.matchedChargingPoints}</span>
            </div>
          </div>
          <div className="content">
            <span>
              <PlugBlackIcon />
            </span>
            <div>{t('noOfChargersNotAssigned')}</div>
            <div className="count">
              <span>{chargerAssignCounts.unMatchedChargingPoints}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Vlbc management page
 */
function VlbcManagement() {
  const { t, i18n } = useTranslation('vlbc');
  const dispatch = useAppDispatch();
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    header: false,
    content: null,
    notificationModal: false,
  });
  const [chargerAssignCounts, setChargerAssignCounts] = useState<AssignChargersCount>({
    matchedChargingPoints: 0,
    unMatchedChargingPoints: 0,
    vlbcTotalCount: 0,
  });

  /**
   * Close Add vlbc modal
   */
  const closeVlbcModal = () => {
    setModalBoxStatus('close');
  };

  /**
   * Fetch Chargers Assign Count
   */
  const fetchChargersAssignCount = async () => {
    vlbcApi
      .assignChargersCount()
      .then((response) => {
        const chargerCountInfo = response.data;
        setChargerAssignCounts(chargerCountInfo);
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Handle the openAddFacilityModal function
   */
  const openAddVlbcModal = () => {
    setModalBoxDetail({
      title: t('addLoadBalancer'),
      content: (
        <AddVlbc
          handleCancel={() => setModalBoxStatus('close')}
          closeVlbcModal={closeVlbcModal}
          fetchChargersAssignCount={fetchChargersAssignCount}
        />
      ),
      notificationModal: false,
      header: true,
    });
    setModalBoxStatus('open');
  };

  /**
   * handleEdit
   * @param {any} vlbcId - The vlbcId
   * @param {any} event - The event
   */
  const handleEdit = (vlbcId: string, event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation();
    setModalBoxDetail({
      title: t('editLoadBalancer'),
      content: (
        <EditVlbc
          handleCancel={() => setModalBoxStatus('close')}
          closeVlbcModal={closeVlbcModal}
          vlbcId={vlbcId}
          fetchChargersAssignCount={fetchChargersAssignCount}
        />
      ),
      notificationModal: false,
      header: true,
    });
    setModalBoxStatus('open');
  };

  /**
   * handleDelete
   * @param {any} vlbcId - The vlbcId
   */
  const handleDelete = (vlbcId: string) => {
    vlbcApi
      .deleteVlbc(vlbcId)
      .then(() => {
        dispatch(
          removeVlbc({
            vlbcId,
          }),
        );
        dispatch(
          setNotificationMessage({
            type: 'success',
            notificationDetails: {
              statusCode: 200,
              message: 'Deleted Successfully',
            },
          }),
        );

        dispatch(
          fetchVlbcs({
            page: DefaultApiLimit.page,
            limit: DefaultApiLimit.limit,
          }),
        );
        fetchChargersAssignCount();
      })
      .catch(() => {
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: 500,
              message: 'Something went wrong',
            },
          }),
        );
      });
  };

  /**
   * handle update isActive
   * @param {any} vlbcId - The vlbcId
   * @param {any} isActive - The isActive
   */
  const handleUpdateStatus = (vlbcId: string, isActive: boolean) => {
    vlbcApi
      .updateVlbcStatus(vlbcId, {
        isActive: !isActive,
      })
      .then(() => {
        dispatch(
          updateVlbcStatusRedux({
            vlbcId,
            isActive: !isActive,
          }),
        );
      })
      .catch(() => {
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: 500,
              message: 'Something went wrong',
            },
          }),
        );
      });
  };

  useEffect(() => {
    dispatch(fetchVlbcs({}));
    fetchChargersAssignCount();
  }, []);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('virtualLoadBalancer'),
        icon: <PlusIcon />,
        // count:chargerAssignCounts.vlbcTotalCount
      }),
    );
  }, [t]);

  return (
    <div className={`vlbc-management-wrap ${i18n.language === 'ar' ? 'rtl' : ''}`}>
      <ModalBox
        fullHeightWidth
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
        rightCorner=""
        modalBoxAnimation="modalAnimateToLeft"
      />
      <div className="vlbc-management-header">
        <PageTitle
          title={t('')}
          buttonLabel={t('createLoadBalancer')}
          buttonSvgIcon={<PlusIcon />}
          topLeftContent={<HeaderSupportContent chargerAssignCounts={chargerAssignCounts} />}
          buttonType="submit"
          onClickButton={openAddVlbcModal}
        />
      </div>
      <br />
      <ListVlbc
        handleUpdateStatus={(id, isActive) => handleUpdateStatus(id, isActive)}
        handleEdit={(id, event) => handleEdit(id, event)}
        handleDelete={(id) => handleDelete(id)}
      />
    </div>
  );
}

export default VlbcManagement;
